import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { GenericService } from '@services/generic.service';
import { combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { CompaniesService } from '@services/companies.service';
import { LhcbMigrationPopupService } from '@services/lhcb-migration-popup.service';
import { CompanyModel } from '@data-layer/models/companies';
import { environment } from '@env/environment';
import { enumEnvironmentName } from '@env/enumEnvironmentName';
import { LoggerService } from '@services/logger.service';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { GlobalAlertModel } from '@app/data-layer/models/common';
import { GlobalAlertsRepository } from '@app/data-layer/repositories/global-alerts.repository';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { ProductDetailsModel } from '@data-layer/models/common';
import { FetchProductsByCompany, SharedSelectors } from '@app/shared/shared-state';
import { PoaPriceHelper } from '@helpers/poa-price.helper';

@Component({
  selector: 'app-app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  public isIsfDesktop: boolean;
  public isShowGlobalAlert: boolean;
  public isIsf: boolean;
  public isES: boolean;
  public isLCLDesktop: boolean;
  public isDesktopClass: string = 'is-desktop';
  public isLaptop$: Observable<boolean>;
  public globalAlerts: GlobalAlertModel[] = [];
  public readonly migrationCompany$: Observable<CompanyModel | null>;
  public readonly isAcknowledgingMigration$: Observable<boolean>;
  // #13213 — POA price for the LHCB popup banner comes from the backend product config
  public readonly poaPrice$: Observable<number | null>;

  private isMobile: boolean;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private ga: GoogleAnalyticsService,
    private router: Router,
    private genericService: GenericService,
    private applicationModeService: ApplicationModeService,
    private globalAlertsRepository: GlobalAlertsRepository,
    @Inject(DOCUMENT) private document: Document,
    private companiesService: CompaniesService,
    private loggerService: LoggerService,
    private lhcbMigrationPopupService: LhcbMigrationPopupService,
    private store: Store,
  ) {
    this.migrationCompany$ = this.lhcbMigrationPopupService.activeCompanyForPopup$;
    this.isAcknowledgingMigration$ = this.lhcbMigrationPopupService.isAcknowledging$;
    this.poaPrice$ = combineLatest([this.migrationCompany$, this.store.select(SharedSelectors.getPoaProductDetails)]).pipe(
      map(([company, details]: [CompanyModel | null, ProductDetailsModel | null]) =>
        PoaPriceHelper.extractPrice(details, company ? company.id : null),
      ),
    );
  }

  public onMigrationDiscarded(): void {
    this.lhcbMigrationPopupService.acknowledgeAndClose();
  }

  public onMigrationSignThePoa(): void {
    this.lhcbMigrationPopupService.acknowledgeAndNavigate();
  }

  public ngOnInit(): void {
    this.syncGlobalAlerts();

    // #13213 — fetch the POA price for the LHCB migration popup banner
    this.migrationCompany$
      .pipe(
        filter((company: CompanyModel | null): company is CompanyModel => !!company),
        distinctUntilChanged((previous: CompanyModel, current: CompanyModel) => previous.id === current.id),
        takeUntil(this.destroy$),
      )
      .subscribe((company: CompanyModel) => {
        const cached: ProductDetailsModel | null = this.store.selectSnapshot(SharedSelectors.getPoaProductDetails);
        if (cached !== null && cached.companyId === company.id) {
          return;
        }
        this.store.dispatch(new FetchProductsByCompany({ productCode: enumBillItemProductCode.POA, companyId: company.id }));
      });

    this.ga.setDefaultConsent();
    this.ga.setUser();

    // TODO update this temporary logic later
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        withLatestFrom(this.genericService.isDesktop$),
        tap(([route, isDesktop]) => {
          this.calcIsIsfDesktop(isDesktop);
          this.calcIsIsf();
          this.calcIsES();
          this.calcIsLCLDesktop(isDesktop);
          this.calcIsDesktopClass();
          this.syncGlobalAlerts();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.genericService.isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.isLaptop$ = this.genericService.isLaptop$;
    this.genericService.isDesktop$
      .pipe(
        tap((isDesktop: boolean) => {
          this.calcIsIsfDesktop(isDesktop);
          this.calcIsIsf();
          this.calcIsES();
          this.calcIsLCLDesktop(isDesktop);
          this.calcIsDesktopClass();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    // load external company&user ids and use them for HotJar & AppInsights
    this.companiesService.companyChange$
      .pipe(
        switchMap((companyChange: { previous: CompanyModel; current: CompanyModel }) => {
          const companyId: number = companyChange?.current?.id || this.companiesService.getUser()?.defaultCompanyId;
          if (!!companyId && !!companyChange?.previous) {
            return this.companiesService.getCompanyExternalIds(companyId);
          } else {
            return of(EMPTY);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!!this.companiesService.companyExternalIds || !!this.companiesService.userExternalId) {
          if (environment.name === enumEnvironmentName.PROD) {
            this.companiesService.prepareHotjarData();
          }

          this.loggerService.clearAuthenticatedUserContext();
          this.loggerService.setAuthenticatedUserContext(
            this.companiesService.userExternalId,
            this.companiesService.companyExternalIds?.externalCompanyId,
          );
        }
      });
  }

  public ngOnDestroy(): void {
    this.ga.removeUser();

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public setIsShowGlobalAlert(value: boolean) {
    this.isShowGlobalAlert = value;
  }

  public onDismissGlobalAlert(alertId: number) {
    this.globalAlertsRepository
      .deleteGlobalAlert(alertId)
      .pipe(
        switchMap(() => this.globalAlertsRepository.getGlobalAlerts()),
        takeUntil(this.destroy$),
      )
      .subscribe((globalAlerts) => {
        this.globalAlerts = globalAlerts;
      });
  }

  public get isActiveUserMode() {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.USER;
  }

  public get isHasIsfOcr(): boolean {
    const isWithUserWithIsfOcrFlag = this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.isfOcr);

    return isWithUserWithIsfOcrFlag && !this.isMobile;
  }

  private calcIsIsfDesktop(isDesktop: boolean): void {
    this.isIsfDesktop = isDesktop && !!localStorage.getItem('wizardDesktop') && this.router.url.includes('isf/');
  }

  private calcIsIsf(): void {
    this.isIsf = this.router.url.includes('isf/');
  }

  private calcIsES(): void {
    this.isES = this.router.url.includes('entry-summary/');
  }

  private calcIsLCLDesktop(isDesktop: boolean): void {
    this.isLCLDesktop = isDesktop && this.router.url.includes('/lcl-shipping');
  }

  private calcIsDesktopClass(): void {
    this.isIsfDesktop || this.isLCLDesktop
      ? this.document.body.classList.add(this.isDesktopClass)
      : this.document.body.classList.remove(this.isDesktopClass);
  }

  private syncGlobalAlerts() {
    this.setIsShowGlobalAlert(false);

    this.globalAlertsRepository
      .getGlobalAlerts(false)
      .pipe(takeUntil(this.destroy$))
      .subscribe((globalAlerts) => {
        this.globalAlerts = globalAlerts;
        setTimeout(() => {
          this.setIsShowGlobalAlert(true);
        }, 0);
      });
  }
}
